import React, {useEffect, useState, useRef} from "react";
import loaderimage from "../../images/giphy.gif";
import axios from "axios";
import {Formik, Field, Form, ErrorMessage, useField, useFormikContext} from "formik";
import {useNavigate, useParams} from "react-router-dom";
import {Container, Row, Col, Button, Card} from "react-bootstrap";
import * as Yup from "yup";
import ConfirmationModal from "../shared/ConfirmationModal";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const _ = require('lodash');

export const CitySearchDropdown = ({ name, label, initialValue= "", ...props }) => {
  console.log(initialValue);
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [searchTerm, setSearchTerm] = useState(field.value || "");
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(field.value || "");
  }, [field.value]);

  useEffect(() => {
    const fetchCities = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}cities/showallcities`);
        setCities(response.data);
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCities(cities);
    } else {
      const filtered = cities.filter(city =>
        city.city_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
    }
  }, [searchTerm, cities]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);

    if (selectedCity && e.target.value !== selectedCity.city_name) {
      setSelectedCity(null);
      setFieldValue(name, '');
    }
  };

  const handleSelectCity = (city) => {
    setSelectedCity(city);
    setSearchTerm(city.city_name);
    setIsOpen(false);

    setFieldValue(name, city.city_name);
  };

  return (
    <div className="form-group mb-3" ref={dropdownRef}>
      {label && <label htmlFor={props.id || name} className="form-labe text-capitalize">{label}:</label>}
      <div className="input-group">
        <input
          type="text"
          className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
          {...props}
          placeholder="Search for a city..."
          value={searchTerm}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
          autoComplete="off"
        />
      </div>

      {isOpen && (
        <div className="dropdown-menu w-25 show mt-1 shadow">
          {isLoading ? (
            <div className="text-center py-2">Loading cities...</div>
          ) : filteredCities.length > 0 ? (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filteredCities.map((city) => (
                <button
                  key={city.city_id}
                  type="button"
                  className="dropdown-item "
                  onClick={() => handleSelectCity(city)}
                >
                  {city.city_name}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-2">No cities found</div>
          )}
        </div>
      )}

      {meta.touched && meta.error ? (
        <div className="invalid-feedback d-block">{meta.error}</div>
      ) : null}
    </div>
  );
};

const SkillsAutocomplete = ({name, value, onChange, onBlur, skillsList}) => {
const initialValue = Array.isArray(value)
  ? value
  : typeof value === "string"
  ? value.split(",")
  : [];
  const [inputValue, setInputValue] = useState('');
  const [selectedSkills, setSelectedSkills] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedSkills]);

  useEffect(() => {
    const skillsArray = Array.isArray(value)
    ? value
    : typeof value === "string"
    ? value.split(",")
    : [];

    const mappedSkills = skillsArray.map(skill => {
      if (typeof skill === 'object' && skill.skill_id) {
        return skill;
      }
      const foundSkill = skillsList.find(s => s.skill_id === skill);
      return foundSkill || null;
    }).filter(Boolean);

    setSelectedSkills(mappedSkills);
  }, [value, skillsList]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.trim()) {
      const filtered = skillsList.filter(skill => {
        const searchTerm = value.toLowerCase();
        const alreadySelected = selectedSkills.some(
          selected => selected.skill_id === skill.skill_id
        );

        return !alreadySelected && (
          skill.skill_name.toLowerCase().includes(searchTerm) ||
          skill.skill_shortname.toLowerCase().includes(searchTerm) ||
          skill.skill_tag.toLowerCase().includes(searchTerm)
        );
      });
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const addSkill = (skill) => {
    if (!selectedSkills.some(selected => selected.skill_id === skill.skill_id)) {
      const newSkills = [...selectedSkills, skill];
      setSelectedSkills(newSkills);
      onChange(newSkills);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const removeSkill = (skillToRemove) => {
    const newSkills = selectedSkills.filter(
      skill => skill.skill_id !== skillToRemove.skill_id
    );
    setSelectedSkills(newSkills);
    onChange(newSkills);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      if (suggestions.length > 0) {
        addSkill(suggestions[0]);
      }
    }
  };

  const renderSkillBadge = (skill) => {
    const tags = skill?.skill_tag?.split(',').slice(0, 2).map(tag => tag.trim());
    return (
      <span
        key={skill.skill_id}
        className="badge bg-primary me-2 mb-2 d-inline-flex align-items-center"
      >
        <span className="d-flex flex-column">
          <span>{skill.skill_name}</span>
          <small className="text-white-50">
            {tags?.join(' • ')}
          </small>
        </span>
        <button
          type="button"
          className="btn-close btn-close-white ms-2"
          style={{fontSize: '0.5rem'}}
          onClick={() => removeSkill(skill)}
          aria-label="Remove skill"
        />
      </span>
    );
  };

  const renderSuggestionItem = (skill) => {
    return (
      <div
        key={skill.skill_id}
        className="p-2 cursor-pointer hover-bg-light"
        style={{cursor: 'pointer'}}
        onMouseDown={() => addSkill(skill)}
      >
        <div className="d-flex flex-column">
          <span className="fw-medium">{skill.skill_name}</span>
          <small className="text-muted">
            {skill.skill_category} • {skill.skill_shortname}
          </small>
          <small className="text-muted">
            Tags: {skill.skill_tag}
          </small>
        </div>
      </div>
    );
  };

  return (
    <div className="skills-autocomplete">
      <div className="mb-2">
        {selectedSkills.map(renderSkillBadge)}
      </div>
      <div className="position-relative">
        <input
          type="text"
          ref={inputRef}
          name={name}
          className="form-control"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            setTimeout(() => setShowSuggestions(false), 200);
            if (onBlur) onBlur(e);
          }}
          placeholder="Search skills by name, shortname, or tags..."
        />
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="position-absolute w-100 mt-1 shadow bg-white rounded border"
            style={{
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}
          >
            {suggestions.map(renderSuggestionItem)}
          </div>
        )}
      </div>
    </div>
  );
};

const JobDetailsUpdate = () => {
  const {jobid} = useParams();
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [operationType, setOperationType] = useState("");
  const [onConfirm, setOnConfirm] = useState(null);
  const [degrees, setDegrees] = useState([]);

  const jobDetailsInitialState = {
    job_id: jobid,
    number_of_positions: 1,
    job_highlights: stripHtmlTags(""),
    job_description: stripHtmlTags(""),
    skills: "",
    department: "",
    role: "",
    city: "",
    package_: "",
    employment_duration: "",
    job_type: "",
    job_application_internal: "",
    job_external_url: "",
    qualification: "",
    experience: "",
    work_mode: "",
  };
  const validationSchema = Yup.object().shape({
    number_of_positions: Yup.number()
      .required('Number of positions is required')
      .min(1, 'Must be at least 1 position')
      .integer('Must be a whole number'),
    skills: Yup.mixed().required('Skills are required'),
    role: Yup.string().nullable(),
    job_type: Yup.string().required('Job type is required'),
    job_description: Yup.string().required('Job description is required'),
    package_: Yup.string().nullable(),
    qualification: Yup.string().required('Qualification is required'),
    experience: Yup.string().required("Experience is required"),
    city: Yup.string().required('Address is required'),
    department: Yup.string().nullable(),
    job_highlights: Yup.string(),
    job_application_internal: Yup.string(),
    job_external_url: Yup.string(),
    work_mode: Yup.string().required("Work mode is required"),
  });
  const [updateData, setUpdateData] = useState(jobDetailsInitialState);
  const [loader, SetLoader] = useState(false);
  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const jobTypes = [
    {value: '', label: 'Select Job Type'},
    {value: 'full_time', label: 'Full Time'},
    {value: 'part_time', label: 'Part Time'},
    {value: 'contract', label: 'Contract'},
    {value: 'internship', label: 'Internship'},
    { value: 'freelance', label: 'Freelance' },
    { value: 'temporary', label: 'Temporary' },
    { value: 'remote', label: 'Remote' },
    {value: 'NA', label: 'NA'},
  ];

  function stripHtmlTags(text) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    return doc.body.textContent || "";
  }

  const navigate = useNavigate();

  const callApiJobDetailsUpdate = async (values) => {
    try {
      const url = process.env.REACT_APP_API_URL + "job_details/updatejob_details";
      const response = await axios.put(url, values);
      if (response.status === 200) {
        setModalMessage("Updated successfully!");
        setOperationType("OK");
        setOnConfirm(() => () => navigate("/joblist"));
        setShowModal(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAllSkills = async () => {
    try {
      const url = process.env.REACT_APP_API_URL + "skills/showallskills";
      const response = await axios.get(url);
      setSkills(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  const fetchAllDepartments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}department/getdepartments`);
      setDepartments(response.data);
    }
    catch (error) {
      console.log(error);
    }
  }
  const fetchAllDegrees = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}department/getdepartments`);
      setDegrees(response.data);
    }
    catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    const getJobDetailByIDApi = async (jobid) => {
      try {
        SetLoader(true)
        const url = process.env.REACT_APP_API_URL + "job_details/job_detailsbyid/" + jobid;
        const response = await axios.get(url);
        let jobData = response.data[0];

        let skillIds = [];

        if(typeof jobData.skills === 'string') {
          try {
          const parsed = JSON.parse(jobData.skills);
          if (Array.isArray(parsed)) {
            jobData.skills = parsed;
          } else {
            skillIds = jobData.skills.split(',').map(id => id.trim());
          }
        } catch (err) {
          skillIds = jobData.skills.split(',').map(id => id.trim());
        }
      }

      if (skillIds.length > 0) {
        jobData.skills = skillIds
          .map(id => skills.find(skill => skill.skill_id == id))
          .filter(Boolean);
      }

        setUpdateData(response.data[0])
      } catch (error) {
        console.log(error);
      } finally {
        SetLoader(false)
      }
    };
    getJobDetailByIDApi(jobid);
    fetchAllSkills();
    fetchAllDepartments();
    fetchAllDegrees();
  }, [])


  return (
    <>
    <ToastContainer/>
    <Container className="mt-5">
      <Row className="justify-content-center w-110">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Update Job Details</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                validationSchema={validationSchema}
                enableReinitialize={true}
                initialValues={updateData || jobDetailsInitialState}
                onSubmit={async (values) => {
                  console.log(updateData, "||||||", values)
                  if (_.isEqual(values, updateData)) {
                    setModalMessage("No update made");
                    setOperationType("Ok");
                    setShowModal(true);
                  } else {
                    setModalMessage("Are you sure you want to update?");
                    setOperationType("Yes");
                    setOnConfirm(() => () => callApiJobDetailsUpdate(values));
                    setShowModal(true);                    
                  }
                }}
              >
                {() => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="no" className="form-label">
                          No Of Positions:<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="number_of_positions"
                          type="text"
                          className="form-control"
                          placeholder="Enter Number of Positions"
                        />
                        <ErrorMessage
                          name="number_of_positions"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>

                      <Col md={6}>
                        <label htmlFor="company_name" className="form-label">
                          Job Type:<span className="text-danger">*</span>
                        </label>
                        <Field as="select" name="job_type" className="form-control">
                          {jobTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="job_type"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="skills" className="form-label">
                          Skills:<span className="text-danger">*</span>
                        </label>
                        <Field>
                          {({field, form}) => (
                            <SkillsAutocomplete
                              name={field.name}
                              value={form.values.skills}
                              onChange={(skills) => form.setFieldValue('skills', skills)}
                              onBlur={field.onBlur}
                              skillsList={skills}
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="skills"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="work_mode" className="form-label">Work Mode</label><span className="text-danger">*</span>
                        <Field
                        as="select"
                        name="work_mode"
                        className="form-control"
                        >
                          <option value="">--Choose Work Mode--</option>
                          <option value="wfo">Work From Office</option>
                          <option value="wfh">Work From Home</option>
                          <option value="hybrid">Hybrid</option>
                        </Field>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="qualification" className="form-label">
                          Qualification:<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="qualification"
                          type="text"
                          className="form-control"
                          placeholder="Enter Required Qualification"
                        />
                        <ErrorMessage
                          name="qualification"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="experience" className="form-label">
                          Experience (in years):
                        </label><span className="text-danger">*</span>
                        <Field
                          name="experience"
                          type="text"
                          className="form-control"
                          placeholder="Enter Experience (e.g. 4 - 5 years)"
                        />
                         <ErrorMessage
                          name="experience"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="department" className="form-label">
                          Department:
                        </label>
                        <Field
                          as="select"
                          name="department"
                          className="form-control"
                          placeholder="Enter Department"
                        >
                          <option value="">Choose Department</option>
                          {departments.map((department) => (
                            <option key={department.department_id} value={department.department_name}>{department.department_name}</option>
                          ))}
                        </Field>
                        {/* <ErrorMessage
                          name="department"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>

                      <Col md={6}>
                        <label htmlFor="role" className="form-label">
                          Role Name:
                        </label>
                        <Field
                          name="role"
                          type="text"
                          className="form-control"
                          placeholder="Enter Job Role"
                        />
                        {/* <ErrorMessage
                          name="role"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="hr_name" className="form-label">
                          Job Application Internal:
                        </label>
                        <Field
                          name="job_application_internal"
                          type="text"
                          className="form-control"
                          placeholder="Enter Job Application Internal"
                        />
                        {/* <ErrorMessage
                          name="job_application_internal"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>

                      <Col md={6}>
                        <label htmlFor="city" className="form-label">
                          City:<span className="text-danger">*</span>
                        </label>
                        {/* <Field
                          name="city"
                          type="text"
                          className="form-control"
                          placeholder="Enter Address"
                        >
                          {({field, form}) => ( */}
                            {/* <CitySearchDropdown name={field.name} initialValue={updateData.city} /> */}
                            <CitySearchDropdown name="city" initialValue={updateData.city}/>
                            {/* <ErrorMessage name="city" component="div" className="text-danger mt-1" /> */}
                          {/* )}
                        </Field> */}
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="hr_name" className="form-label">
                          Package:
                        </label>
                        <Field
                          name="package_"
                          type="text"
                          className="form-control"
                          placeholder="5 or 4-5 LPA"
                        />
                        {/* <ErrorMessage
                          name="package"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>

                      <Col md={6}>
                        <label htmlFor="hr_name" className="form-label">
                          Job External URL:
                        </label>
                        <Field
                          name="job_external_url"
                          type="text"
                          className="form-control"
                          placeholder="Enter External URL"
                        />
                        {/* <ErrorMessage
                          name="job_external_url"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="job_name" className="form-label">
                          Job Description:<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="job_description"
                          type="text"
                          as="textarea"
                          rows={3}
                          className="form-control"
                        />
                        {/* <ErrorMessage
                          name="job_description"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="job_name" className="form-label">
                          Job Highlights:
                        </label>
                        <Field
                          name="job_highlights"
                          as="textarea"
                          rows={3}
                          type="text"
                          className="form-control"
                        />
                        {/* <ErrorMessage
                          name="job_highlights"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>
                    </Row>
                    <Row className="mt-4">
                      <Col className="text-center">
                        <Button type="submit" variant="success">
                          Update Details
                        </Button>
                        <Button 
      variant="danger" 
      className="ms-3" 
      onClick={() => navigate(-1)}  
    >
      Go Back
    </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loader && (
        <div className="loader">
          <img src={loaderimage} alt="Loader"/>
        </div>
      )}
      {showModal && (
        <ConfirmationModal
          show={showModal}
          onHide={() => setShowModal(false)}
          modalMessage={modalMessage}
          operationType={operationType}
          confirmation={onConfirm}
        />
      )}
    </Container>
  </>
  )
}

export default JobDetailsUpdate
